// core/maintenance — Mode Pemeliharaan: menutup aplikasi untuk semua orang KECUALI Owner,
// supaya deploy/migrasi bisa dijalankan tanpa ada kasir yang menyimpan transaksi di tengah jalan.
//
// BATAS YANG HARUS JUJUR DIKETAHUI: ini penjaga di LAYAR, bukan di basis data. Ia mencegah
// petugas memakai aplikasi, tetapi tidak menghentikan permintaan yang sudah telanjur berjalan,
// dan tidak menutup akses lewat alat lain. Untuk penguncian sungguhan saat migrasi berbahaya,
// tetap perlu penjagaan di sisi basis data (RLS/kunci tabel).
import { supabase } from '@core/supabase';
import { currentUser } from '@core/session';
import { esc } from '@domain/format';

export interface Pemeliharaan {
  on: boolean;
  pesan: string;
  oleh: string | null;
  mulai: string | null;      // ISO
  /** Perkiraan selesai (teks bebas: "±15 menit", "jam 21.00") — dibaca petugas, bukan mesin. */
  perkiraan: string;
}
export const DEF_PEMELIHARAAN: Pemeliharaan = {
  on: false, pesan: 'Aplikasi sedang diperbarui. Mohon tunggu sebentar.',
  oleh: null, mulai: null, perkiraan: '',
};

const KEY = 'pemeliharaan';

/** Baca status TERKINI dari basis data (bukan dari ingatan) — dipakai pemeriksaan berkala. */
export async function bacaPemeliharaan(): Promise<Pemeliharaan> {
  try {
    const { data, error } = await supabase().from('settings').select('value').eq('key', KEY).maybeSingle();
    if (error) throw new Error(error.message);
    return { ...DEF_PEMELIHARAAN, ...((data as { value?: Partial<Pemeliharaan> } | null)?.value ?? {}) };
  } catch { return DEF_PEMELIHARAAN; }   // gagal baca → jangan mengunci orang karena jaringan
}

export async function setPemeliharaan(on: boolean, pesan: string, perkiraan: string): Promise<void> {
  const u = currentUser();
  // HANYA Owner. `is_mgr()` di RLS = Owner ATAU Manager, jadi tanpa guard ini Manager bisa
  // mengunci seluruh staf (dan tirai hanya mengecualikan Owner) — DoS orang dalam (audit P2).
  if (u?.role !== 'Owner') throw new Error('Hanya Owner yang boleh mengubah Mode Pemeliharaan.');
  const nilai: Pemeliharaan = {
    on, pesan: pesan.trim() || DEF_PEMELIHARAAN.pesan, perkiraan: perkiraan.trim(),
    oleh: on ? (u?.nama ?? null) : null, mulai: on ? new Date().toISOString() : null,
  };
  const { error } = await supabase().from('settings')
    .upsert({ key: KEY, value: nilai, updated_at: new Date().toISOString() });
  if (error) throw new Error(error.message);
}

let tirai: HTMLElement | null = null;

/** Tirai layar penuh. Owner tetap bisa bekerja (ia yang sedang men-deploy) — cukup diberi tanda. */
function gambarTirai(p: Pemeliharaan): void {
  const u = currentUser();
  const owner = u?.role === 'Owner';
  if (!p.on) { tirai?.remove(); tirai = null; document.querySelector('#mt_pita')?.remove(); return; }

  if (owner) {
    if (document.querySelector('#mt_pita')) return;
    const pita = document.createElement('div');
    pita.id = 'mt_pita';
    pita.style.cssText = 'position:fixed;left:0;right:0;bottom:0;z-index:9998;background:var(--warn);color:#fff;'
      // Padding bawah menghitung garis home indicator iPhone: tanpa itu seluruh pita
      // (±26px) tenggelam di balik garis dan peringatannya tak terbaca sama sekali.
      + 'padding:6px 12px calc(6px + var(--sab));font-size:12.5px;font-weight:600;text-align:center';
    pita.textContent = '🔧 Mode Pemeliharaan AKTIF — petugas lain tak bisa memakai aplikasi. Matikan bila deploy selesai.';
    document.body.appendChild(pita);
    return;
  }
  if (tirai) return;
  tirai = document.createElement('div');
  tirai.style.cssText = 'position:fixed;inset:0;z-index:9999;background:#0A2A4A;color:#fff;display:flex;'
    + 'align-items:center;justify-content:center;text-align:center;padding:24px';
  tirai.innerHTML = `<div style="max-width:420px;line-height:1.6">
    <div style="font-size:44px">🔧</div>
    <h2 style="margin:8px 0">Sedang dalam pemeliharaan</h2>
    <p style="font-size:14px;opacity:.9">${esc(p.pesan)}</p>
    ${p.perkiraan ? `<p style="font-size:13px;opacity:.8">Perkiraan selesai: <b>${esc(p.perkiraan)}</b></p>` : ''}
    <p style="font-size:12px;opacity:.7;margin-top:14px">Jangan tutup catatan manual Anda — transaksi yang belum sempat
      dimasukkan tetap perlu dicatat setelah aplikasi kembali.</p>
    ${p.oleh ? `<p style="font-size:11.5px;opacity:.6">Dimulai oleh ${esc(p.oleh)}</p>` : ''}</div>`;
  document.body.appendChild(tirai);
}

/**
 * Pantau status tiap `jeda` detik. Diperiksa BERKALA (bukan sekali saat masuk) supaya petugas
 * yang sedang membuka aplikasi ikut terkunci begitu Owner menyalakannya — itulah gunanya:
 * mencegah transaksi masuk di tengah deploy.
 */
export function pantauPemeliharaan(jeda = 45): void {
  const cek = async (): Promise<void> => { gambarTirai(await bacaPemeliharaan()); };
  void cek();
  setInterval(() => { if (!document.hidden) void cek(); }, jeda * 1000);
  document.addEventListener('visibilitychange', () => { if (!document.hidden) void cek(); });
}
